import type {OperatingSystem} from "../../api/models";

// Words shared across the site: the layout around every page, and components many pages use. Plain data, like
// errors.ts: {placeholders} are filled by format() (src/i18n/format.ts), and <tag>…</tag> marks words rich()
// (src/i18n/rich.tsx) turns into a link or other element.
export const common = {
    loading: 'Loading…',
    cancel: 'Cancel',
    copy: 'Copy',
    // Read out by screen readers after the copy button is used.
    copied: 'Copied',
    copyFailed: "Couldn't copy",

    // Other companies' trademarks the products' pages name (components/TrademarkNotices.tsx), in their owners' words:
    // don't reword them. asio is the ASIO SDK license agreement's own notice (§ 3.1 k); vst, Steinberg's VST usage
    // guidelines (§ 15); avid, Avid's trademark usage guidelines; apple, Apple's guidelines for products sold outside
    // the U.S.
    trademarks: {
        label: 'Trademarks',
        asioLogoAlt: 'ASIO Compatible. ASIO is a registered trademark of Steinberg Media Technologies GmbH.',
        vstLogoAlt: 'VST Compatible. VST is a registered trademark of Steinberg Media Technologies GmbH.',
        asio: 'ASIO is a trademark of Steinberg Media Technologies GmbH, registered in Europe and other countries.',
        vst: 'VST is a registered trademark of Steinberg Media Technologies GmbH.',
        avid: 'Avid, Pro Tools and AAX are trademarks or registered trademarks of Avid Technology, Inc. or its subsidiaries in the United States and/or other countries.',
        apple: 'Mac, macOS, Logic Pro and GarageBand are trademarks of Apple Inc., registered in the U.S. and other countries and regions.'
    },

    layout: {
        skipToMain: 'Skip to main content',
        nav: {
            home: 'Home',
            products: 'Products',
            documentation: 'Documentation',
            myLicenses: 'My licenses',
            // Signed in: their support tickets. Signed out, as in the footer: /contact.
            support: 'Support',
            contact: 'Contact us',
            administration: 'Administration',
            signIn: 'Sign in'
        },
        // The signed-in user's menu, under their name.
        accountMenu: {
            profile: 'Profile',
            myLicenses: 'My licenses',
            myOrders: 'My orders',
            supportTickets: 'Support tickets',
            signOut: 'Sign out'
        },
        // Shown to administrators only, at the top of every page.
        maintenanceOn: 'Maintenance mode is on: only administrators can see the site. <link>Turn it off</link>',
        // Signed in, when mail to the account's address bounces.
        emailUndeliverable: "We can't deliver email to {email}. <link>Update your email address</link> so you can get password resets and sign-in codes.",
        copyright: '© {year} {site}',
        // In the footer, on every page: /contact, a message to us (anyone can send one).
        contact: 'Contact us',
        // In the footer, beside it: /press, the press kit.
        press: 'Press',
        // The footer's links, and the label screen readers give their group.
        legalNav: 'Legal',
        terms: 'Terms',
        privacy: 'Privacy',
        refunds: 'Refunds',
        cookies: 'Cookies',
        accessibility: 'Accessibility',
        aiPolicy: 'AI policy'
    },

    cookieNotice: {
        // The label screen readers give the notice.
        label: 'Cookie notice',
        text: 'This site uses only essential cookies and browser storage, to keep you signed in and remember your settings. No tracking or advertising. <link>Learn more</link>',
        dismiss: 'OK'
    },

    // The light/dark color scheme menu in the navbar.
    theme: {
        // For screen readers: the menu's button shows only an icon.
        current: 'Color mode: {mode}',
        light: 'Light',
        dark: 'Dark',
        auto: 'Auto'
    },

    operatingSystems: {
        windows: 'Windows',
        macos: 'macOS',
        linux: 'Linux',
        any: 'Any OS'
    } satisfies Record<OperatingSystem, string>,

    adminOnly: 'This area is for site administrators.',

    notFound: {
        title: 'Page not found',
        text: "There's nothing at this address.",
        home: 'Go to the home page'
    },

    maintenance: {
        title: 'Maintenance',
        text: 'The website is currently undergoing maintenance. Please check back later.'
    }
};

export type CommonMessages = typeof common;
